// Where you are, in words — dispatched on the WORLD you are standing on.
//
// The frontier's ground functions (biomeAt, the landmark table, the
// settlement at the origin) were written when there was one planet, and
// every sentence that named a place called them directly. Meridian's
// coordinates overlap the frontier's, so those sentences kept composing:
// the HUD chip called a paved terrace "fen", the rescue board put a
// capital hull "in the settlement", and a Scout charted the Wreck without
// leaving the Prom. Well-formed, and false.
//
// So place words are a function of (world, x, z) here, once, and both
// channels read this module. A world with no landmarks has no regions; a
// paved world names its districts rather than a biome it does not have.

import { cityGroundAt } from "./city-terrain";
import { LANDMARKS, type Landmark } from "./landmarks";
import { biomeAt } from "./terrain";
import { planFor, surfaceIdFor } from "./world-plan";
import { asWorldId, type WorldId } from "./worlds";

/**
 * The named places of a world. The authored landmark table is the
 * frontier's — the Wreck, the Geyser Field, the Obelisk — and Meridian's
 * places are architecture (the Prom, the port, the Understreet), which
 * are rooms and plan features rather than open-ground marks. A world
 * with no landmarks has no charted regions at all, which is exactly the
 * rule the Scout's regionAt now enforces.
 */
export function landmarksFor(world: WorldId | undefined): readonly Landmark[] {
  return asWorldId(world) === "frontier" ? LANDMARKS : [];
}

/**
 * How far from a world's origin its town reaches. ONE authority: every
 * sentence that says "in town" and every state word that means "parked
 * in town" reads this, so a hull can never be dark and in the settlement
 * in the same breath.
 */
export const TOWN_RADIUS_M: Record<WorldId, number> = {
  // The settlement plateau: the street, its facades, the ground around
  // them. The place phrase has always drawn the town at this radius.
  frontier: 250,
  // Meridian's crown — the flat built ground the Prom and the port stand
  // on, out to where the terraces begin (city-terrain's crown blend).
  meridian: 900,
};

/** The name of the town at a world's origin. */
export function townNameFor(world: WorldId | undefined): string {
  return asWorldId(world) === "meridian" ? "the crown" : "the settlement";
}

/**
 * The HUD chip's home anchor: the place a walker measures their distance
 * from. The glossary reserves "Settlement" for the frontier's town.
 */
export function townChipNameFor(world: WorldId | undefined): string {
  return asWorldId(world) === "meridian" ? "The Crown" : "Settlement";
}

/** Is this point inside the town at the world's origin? */
export function inTown(world: WorldId | undefined, x: number, z: number): boolean {
  return Math.hypot(x, z) <= TOWN_RADIUS_M[asWorldId(world)];
}

/**
 * Does this world's ground yield and bite? Read off the world's own
 * surface plan, so "which worlds have wilds" is answered in exactly one
 * place (the plan) and every system that must not run on paved ground —
 * gathering, the Edge, the ecology, homesteading, place-knowledge — asks
 * the same question of the same authority.
 */
export function worldHasWilds(world: WorldId | undefined): boolean {
  return planFor(surfaceIdFor(asWorldId(world))).wilds === true;
}

/**
 * The word for the ground under your boots. The frontier answers with a
 * biome, because the frontier is wilds; Meridian answers with the band of
 * the city it is — crown, terraces, basin — because it is paved and
 * drained and has no biomes to name.
 */
export function groundWordAt(world: WorldId | undefined, x: number, z: number): string {
  if (asWorldId(world) === "meridian") {
    const ground = cityGroundAt(x, z);
    return ground === "crown" ? "the crown" : ground === "terraces" ? "the terraces" : "the basin";
  }
  return biomeAt(x, z);
}
